import React, { useState } from 'react';
import { api, useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Sparkles, Droplets, Flame, X } from 'lucide-react';

const DebriefExtraMiles = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [debrief, setDebrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!user || dismissed) return null;

  const fetchDebrief = async () => {
    setLoading(true);
    try {
      const res = await api.get('/debrief/daily');
      setDebrief(res.data.debrief);
    } catch {
      setDebrief({ message: language === 'fr' ? 'Impossible de g\u00e9n\u00e9rer le debrief pour le moment.' : 'Unable to generate debrief right now.', stats: {} });
    } finally {
      setLoading(false);
    }
  };

  if (!debrief) {
    return (
      <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-orange-500/30 cursor-pointer hover:border-orange-500/50 transition-colors" data-testid="debrief-trigger">
        <CardContent className="p-4">
          <Button
            onClick={fetchDebrief}
            disabled={loading}
            variant="ghost"
            className="w-full h-auto py-3 flex items-center gap-3 text-left"
            data-testid="debrief-generate-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-orange-400 flex-shrink-0" />
            ) : (
              <Sparkles className="w-5 h-5 text-orange-400 flex-shrink-0" />
            )}
            <div>
              <p className="font-heading text-sm uppercase tracking-wider text-orange-400">
                {language === 'fr' ? 'Debrief YUMIX' : 'YUMIX Debrief'}
              </p>
              <p className="text-xs text-muted-foreground">
                {loading
                  ? (language === 'fr' ? 'YUMIX analyse ta journ\u00e9e...' : 'YUMIX is analyzing your day...')
                  : (language === 'fr' ? 'Clique pour voir ton r\u00e9sum\u00e9 du jour' : 'Click to see your daily summary')}
              </p>
            </div>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { stats } = debrief;

  return (
    <Card className="bg-gradient-to-br from-orange-500/10 via-card to-amber-500/5 border-orange-500/30" data-testid="debrief-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-400" />
            <span className="font-heading text-sm uppercase tracking-wider text-orange-400">
              {language === 'fr' ? 'Debrief YUMIX' : 'YUMIX Debrief'}
            </span>
          </div>
          <button onClick={() => setDismissed(true)} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stats row */}
        {stats.meals_logged !== undefined && (
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2 rounded-lg bg-green-500/10">
              <p className="text-lg font-bold text-green-400">{stats.protein_g || 0}g</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                {language === 'fr' ? `Prot / ${stats.target_protein || '?'}g` : `Prot / ${stats.target_protein || '?'}g`}
              </p>
            </div>
            <div className="text-center p-2 rounded-lg bg-orange-500/10">
              <Flame className="w-4 h-4 text-orange-400 mx-auto" />
              <p className="text-lg font-bold text-orange-400">{stats.calories || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">cal</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-blue-500/10">
              <Droplets className="w-4 h-4 text-blue-400 mx-auto" />
              <p className="text-lg font-bold text-blue-400">{stats.water_glasses || 0}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                {language === 'fr' ? 'verres' : 'glasses'}
              </p>
            </div>
          </div>
        )}

        {/* AI Message */}
        <p className="text-sm text-foreground/90 leading-relaxed italic">
          "{debrief.message}"
        </p>
      </CardContent>
    </Card>
  );
};

export default DebriefExtraMiles;
